// OPPSUMMERINGSLINJENE — det operative i ÉN setning, i vanlig språk.
//
// ══ HVORFOR DENNE FILA FINNES (UX-RAPPORTEN J3) ═════════════════════════════
// Rapporten fant at node- og sekvenskortene åpner med fagjargong uten hierarki:
// jegeren må lese spenninger, utladningskurve-forbehold, «bein» og «kandidat»
// før han vet det ene han kom for — beveger noe seg, og lever nodene mine?
// Øverste linje på hvert kort skal derfor svare på DET, alene, i vanlig språk:
//
//   «Bevegelse oppdaget — 3 utløsninger på 2 noder, mot ØST, for 5 min siden»
//   «Node OK — livstegn 4 min, batteri målt OK»
//
// Alt teknisk står fortsatt på kortet — bak «Vis detaljer», aldri fjernet.
//
// ══ HVORFOR REN OG TESTET ══════════════════════════════════════════════════
// Denne ene setningen er det som leses i mørket, ofte som det ENESTE på kortet.
// En oppsummering som pynter på usikkerheten («mot ØST» når retningen er
// ubestemt) ville vært verre enn jargongen den erstatter. Derfor er reglene
// rene funksjoner med tester, ikke strengbygging inne i tegnekoden.
import { NIVAA_NAVN, formaterAlder, type SensorNivaa } from '@skade/sensorflate';
import type { MaltEllerAnslatt } from '@skade/contracts';
import { himmelretning } from './format.js';
import type { Retningsutsagn } from './sekvens.js';

/**
 * Alder som «for 5 min siden». Gjenbruker pakkens `formaterAlder` for selve
 * tallet, så kortet og oppsummeringen aldri kan si to ulike aldre. To
 * spesialtilfeller: under ett sekund er «nå» (ikke «for nå siden»), og over et
 * døgn er tallet uinteressant — tilstanden er «gammelt».
 */
export function forSiden(alderMs: number): string {
  if (!Number.isFinite(alderMs) || alderMs < 1000) return 'nå';
  if (alderMs >= 86_400_000) return 'for over et døgn siden';
  return `for ${formaterAlder(alderMs)} siden`;
}

// ── Sekvenskortet ───────────────────────────────────────────────────────────

export interface SekvensoppsummeringInn {
  /** Antall utløsninger i kandidaten. */
  utlosninger: number;
  /** Antall UNIKE noder som utløste. */
  noder: number;
  /** `retningsutsagn()` for sekvensen — `null` når kontrakten nektet (ett punkt). */
  retning: Retningsutsagn | null;
  /** Alder på SISTE utløsning (ms). */
  alderMs: number;
}

/**
 * Én setning om sekvensen. Regelen fra resten av appen gjelder også her:
 * retningen sies BARE når den er bestemt — «retning ubestemt» er det ærlige
 * svaret ellers, og «(grovt)» følger med når bare himmelretningen holder.
 */
export function sekvensoppsummering(inn: SekvensoppsummeringInn): string {
  const alder = forSiden(inn.alderMs);
  if (inn.retning === null && inn.utlosninger === 1) {
    // Ett punkt: «bevegelse» ville påstått en retning som ikke finnes.
    return `Én utløsning — 1 node, ingen retning fra ett punkt, ${alder}`;
  }
  const teller =
    `${inn.utlosninger} utløsning${inn.utlosninger === 1 ? '' : 'er'} på `
    + `${inn.noder} node${inn.noder === 1 ? '' : 'r'}`;
  const retning =
    inn.retning === null
      ? 'kan ikke analyseres som sekvens'
      : inn.retning.slag === 'bestemt'
        ? `mot ${himmelretning(inn.retning.kursDeg)}${inn.retning.grov ? ' (grovt)' : ''}`
        : 'retning ubestemt';
  return `Bevegelse oppdaget — ${teller}, ${retning}, ${alder}`;
}

// ── Nodekortet ──────────────────────────────────────────────────────────────

export interface NodeoppsummeringInn {
  /** Nivået fra `sensorStatus()` — flatas samlede dom over noden. */
  nivaa: SensorNivaa;
  /** Alder på siste LIVSTEGN (ms). `null` = aldri hørt. */
  livstegnAlderMs: number | null;
  /**
   * Batteriet, kokt ned til det ene jegeren trenger i én linje: er det noe å
   * bekymre seg for, og er tallet MÅLT eller anslått? `null` = ingen
   * batteridata (node vi bare kjenner gjennom utløsninger).
   */
  batteri: { kilde: MaltEllerAnslatt; advarsel: boolean } | null;
}

/**
 * Én setning om noden. Nivåordet er `NIVAA_NAVN` — SAMME ord som resten av
 * flata bruker, så oppsummeringen og detaljene aldri kan si to ulike ting om
 * samme node. (Rapportens «TAPT» er nivået NEDE i flatas ordforråd.)
 */
export function nodeoppsummering(inn: NodeoppsummeringInn): string {
  const livstegn =
    inn.livstegnAlderMs === null
      ? 'aldri hørt livstegn'
      : `livstegn ${formaterAlder(inn.livstegnAlderMs)}`;
  const deler = [livstegn];
  if (inn.batteri) {
    // «målt OK» og ikke bare «OK»: forskjellen på et målt og et anslått
    // batteritall er dager (flat utladningskurve), og den skal stå i linja.
    deler.push(inn.batteri.advarsel
      ? `batteriadvarsel (${inn.batteri.kilde})`
      : `batteri ${inn.batteri.kilde} OK`);
  }
  return `Node ${NIVAA_NAVN[inn.nivaa]} — ${deler.join(', ')}`;
}
